import { inject, Injectable, signal, computed, effect } from '@angular/core';
import { Router } from '@angular/router';
import { UsersService } from '../app/api/api/users.service';
import { HashService } from '../app/api/api/hash.service';
import {
  ApiUserLoginRequest,
  ModelsUser,
  ApiUserLoginResponse,
} from '../app/api/model/models';
import { TranslocoService } from '@jsverse/transloco';
import dayjs from 'dayjs/esm';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { RegistryService } from './RegistryService';

export const USER_ROLE_OWNER = 'owner';
export const USER_ROLE_MANAGER = 'manager';
export const USER_ROLE_EMPLOYEE = 'employee';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private usersApi = inject(UsersService);
  private hashApi = inject(HashService);
  private transloco = inject(TranslocoService);
  private toast = inject(MessageService);
  private router = inject(Router);
  private registry = inject(RegistryService);

  // Signals for modern state management
  public currentUser = signal<ModelsUser | undefined>(undefined);
  public bisiToken = signal<string | undefined>(
    localStorage.getItem('bisi-token') ?? undefined,
  );

  // Backward compatibility alias
  public user = computed(() => this.currentUser());

  public current_locale = signal(localStorage.getItem('lang') || 'he_il');
  public langCode = computed(() =>
    this.current_locale().split('_')[0].toLowerCase(),
  );
  public role_id = computed(() => this.currentUser()?.role);

  public currentCompanyId = computed(() => this.currentUser()?.company_id);
  public currentLocations = computed(() => this.currentUser()?.locations ?? []);
  public currentLocationId = signal<number | undefined>(
    sessionStorage.getItem('locationId')
      ? Number(sessionStorage.getItem('locationId'))
      : undefined,
  );
  public inProperLocation() {
    return (
      (this.currentUser()?.locations?.length ?? 0) <= 1 ||
      this.currentLocationId() !== undefined
    );
  }

  // Persistence effect for the current location (session)
  private locationEffect = effect(() => {
    const locId = this.currentLocationId();
    if (locId) {
      sessionStorage.setItem('locationId', locId.toString());
    } else {
      sessionStorage.removeItem('locationId');
    }
  });

  public rtl = signal(localStorage.getItem('dir') === 'rtl');
  public initials = computed(() => {
    const user = this.currentUser();
    if (!user) return '';
    const fn = user.first_name ?? '';
    const ln = user.last_name ?? '';
    return (fn.charAt(0) + ln.charAt(0)).toUpperCase();
  });

  constructor() { }

  isLoggedIn(): boolean {
    return !!this.bisiToken();
  }

  getToken(): string | undefined {
    return this.bisiToken();
  }

  setToken(token: string) {
    localStorage.setItem('bisi-token', token);
    this.bisiToken.set(token);
  }

  logout() {
    localStorage.removeItem('bisi-token');
    localStorage.removeItem('lang');
    localStorage.removeItem('dir');
    this.bisiToken.set(undefined);
    this.currentUser.set(undefined);
    sessionStorage.clear();
    this.router.navigate(['/login']);
  }

  async loadUser(): Promise<void> {
    try {
      const response = await firstValueFrom(this.usersApi.apiUserGet());
      if (response && response.status !== false && response.data) {
        this.currentUser.set(response.data);
        this.syncSettings();
      }
    } catch (error) {
      console.error('Failed to load user', error);
      this.logout();
      throw error;
    }
  }

  private syncSettings() {
    const user = this.currentUser();
    if (!user) return;

    // Handle direction and language based on user preference or defaults
    // Note: ModelsUser doesn't explicitly have a locale property in the schema I saw,
    // but it has language_id and language.
    const lang =
      user.language?.locale || localStorage.getItem('lang') || 'he_il';
    this.updateLanguage(lang);

    // FIXME: ModelsCompany doesn't have a logo property in the current schema.
    // Logos may need to be handled via services or separate configurations.
  }

  public updateLanguage(lang: string) {
    localStorage.setItem('lang', lang);
    this.current_locale.set(lang);
    this.transloco.setActiveLang(lang);
    const dir = lang === 'he_il' ? 'rtl' : 'ltr';
    this.rtl.set(dir === 'rtl');
    localStorage.setItem('dir', dir);
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
    dayjs.locale(lang.split('_')[0]);
  }

  login(phone: string) {
    const request: ApiUserLoginRequest = { phone };
    return this.usersApi.apiUsersLoginPost(request);
  }

  private async handleLoginResponse(r: ApiUserLoginResponse, redirect: boolean): Promise<ApiUserLoginResponse> {
    if (r.token) {
      this.setToken(r.token);
      let user = r.user;
      if (!user) {
        const response = await firstValueFrom(this.usersApi.apiUserGet());
        if (response && response.status !== false && response.data) {
          user = response.data;
        }
      }

      if (user) {
        const roles = (user.role || '').split(',').map(role => role.trim().toLowerCase());
        const hasAccess = roles.includes(USER_ROLE_OWNER) || roles.includes(USER_ROLE_MANAGER);
        if (!hasAccess) {
          this.logout();
          alert(this.transloco.translate('Access denied'));
          throw new Error('Access denied: Unauthorized role');
        }
        this.currentUser.set(user);
        this.updateLanguage(user.language?.locale || 'he_il');
      } else {
        this.logout();
        throw new Error('User not found');
      }

      await this.registry.loadServices();
      if (redirect) {
        await this.router.navigateByUrl('/dashboard');
      }
    }
    return r;
  }

  async loginByPhone(phone: string, redirect: boolean = true) {
    try {
      const r = await firstValueFrom(this.login(phone));
      return await this.handleLoginResponse(r, redirect);
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  async loginByHash(hash: string, redirect: boolean = true) {
    try {
      const r = await firstValueFrom(this.hashApi.apiHashLoginPost({ hash }));
      return await this.handleLoginResponse(r, redirect);
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  // Placeholder for location switching if the API supports it
  changeLocation(locationId: number) {
    // Implement when API endpoint is clarified
    this.toast.add({
      severity: 'info',
      summary: 'Info',
      detail: `Switching to location ${locationId} (API Integration Pending)`,
    });
  }
}
